import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { nanoid } from 'nanoid';

interface BearStoreState {
  warnMap: Record<string, boolean>;
  upsertWarnItem: (warnKey: string, shouldWarn: boolean) => void;
  removeWarnItem: (warnKey: string) => void;
}

const useBearStore = create<BearStoreState>()((set) => ({
  warnMap: {},
  upsertWarnItem: (warnKey, shouldWarn) => {
    return set(({ warnMap }) => {
      return { warnMap: { ...warnMap, [warnKey]: shouldWarn } };
    });
  },

  removeWarnItem: (warnKey) => {
    return set(({ warnMap }) => {
      const { [warnKey]: _, ...rest } = warnMap;
      return {
        warnMap: rest,
      };
    });
  },
}));

function useShowWarning(shouldWarn?: boolean) {
  const removeWarnItem = useBearStore((state) => state.removeWarnItem);
  const upsertWarnItem = useBearStore((state) => state.upsertWarnItem);

  const [warnKey] = useState(() => nanoid());

  const showWarning = useBearStore(({ warnMap }) => {
    const warnActiveKeys = Object.keys(warnMap).filter((key) => warnMap[key]);
    const hasTrue = warnActiveKeys.some((key) => warnMap[key]) && warnActiveKeys.length > 0;
    return hasTrue;
  });

  useEffect(() => {
    if (shouldWarn) {
      upsertWarnItem(warnKey, shouldWarn);
    } else {
      removeWarnItem(warnKey);
    }

    return () => {
      removeWarnItem(warnKey);
    };
  }, [warnKey, shouldWarn, upsertWarnItem, removeWarnItem]);

  return showWarning;
}

export default useShowWarning;
